import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone, MapPin, Clock, Building2, Home as HomeIcon, Stethoscope } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { HOW_IT_WORKS } from "@/pages/ServiceArea";
import NotFound from "@/pages/NotFound";
import { AREAS } from "@/data/areas";
import { townPage, TOWN_PAGE_SLUGS } from "@/data/townPages";

const AUDIENCES = {
  office: { to: "/office-vending", label: "Offices & workplaces", Icon: Building2 },
  apartment: { to: "/apartment-building-vending", label: "Apartment buildings", Icon: HomeIcon },
  medical: { to: "/medical-office-vending", label: "Medical offices", Icon: Stethoscope },
};

const TownPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const town = townPage(slug);
  if (!town) return <NotFound />;
  const nearby = town.nearby.map((s) => AREAS.find((a) => a.slug === s)).filter(Boolean);

  return (
    <div data-testid={`town-page-${town.slug}`}>
      <section className="relative overflow-hidden pt-36 pb-20 lg:pt-48 lg:pb-28">
        <div aria-hidden="true" className="absolute -top-32 -right-40 h-[30rem] w-[30rem] rounded-full bg-vortex-blue/15 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <MaskedLine delay={0.1}>
            <Overline>{`Service Area — ${town.county}`}</Overline>
          </MaskedLine>
          <h1 className="mt-8 font-heading font-black tracking-tighter leading-[1.02] text-4xl sm:text-5xl lg:text-6xl" data-testid="town-heading">
            <MaskedLine delay={0.25}>SMART COOLER VENDING IN </MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-vortex-cyan">{`${town.name.toUpperCase()}, PA`}</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.55}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">{town.intro}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                data-testid="town-call-link"
                href="tel:+16109838060"
                className="group flex items-center gap-3 rounded-full bg-vortex-cyan px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_40px_rgba(92,179,226,0.5)]"
              >
                <Phone size={15} />
                Call (610) 983-8060
              </a>
              <button
                data-testid="town-contact-button"
                onClick={() => navigate("/contact")}
                className="group flex items-center gap-3 rounded-full border border-white/15 px-8 py-4 font-mono text-xs tracking-[0.2em] uppercase text-slate-300 transition-all duration-300 hover:border-vortex-cyan/60 hover:text-vortex-cyan"
              >
                Send a message
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5 bg-vortex-surface/20" data-testid="town-local">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <Overline>{`Serving ${town.name}`}</Overline>
              <h2 className="mt-5 font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl">{`Where we work in ${town.name}.`}</h2>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-8">
              <div className="max-w-2xl space-y-5 text-base leading-relaxed text-slate-400">
                {town.local.map((p) => <p key={p}>{p}</p>)}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5" data-testid="town-audiences">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <Reveal>
            <Overline>{`Who We Serve in ${town.name}`}</Overline>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
            {town.audiences.map((key, i) => {
              const { to, label, Icon } = AUDIENCES[key];
              return (
                <Reveal key={key} delay={0.06 * i}>
                  <Link
                    to={to}
                    data-testid={`town-audience-${key}`}
                    className="group flex items-center justify-between border border-white/10 px-6 py-6 transition-colors duration-300 hover:border-vortex-cyan/50"
                  >
                    <span className="flex items-center gap-4">
                      <Icon size={20} className="text-vortex-cyan" />
                      <span className="font-heading text-lg font-bold tracking-tight text-white">{label}</span>
                    </span>
                    <ArrowUpRight size={18} className="text-slate-500 transition-colors duration-300 group-hover:text-vortex-cyan" />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-vortex-surface/20" data-testid="town-how-service-works">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <Reveal>
            <Overline>How Service Works</Overline>
            <h2 className="mt-6 font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl">
              No cost to your location. We handle the rest.
            </h2>
          </Reveal>
          <div className="mt-12">
            {HOW_IT_WORKS.map(([title, body], i) => (
              <Reveal key={title} delay={0.05 * i}>
                <div className="flex flex-col gap-3 border-t border-white/10 py-7 sm:flex-row sm:items-start sm:gap-10">
                  <span className="font-mono text-sm tracking-[0.25em] text-vortex-cyan">{`0${i + 1}`}</span>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold tracking-tight text-white sm:text-xl">{title}</h3>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">{body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-white/10" />
          </div>
          <Reveal delay={0.15}>
            <div className="mt-12 flex items-center gap-5 border border-white/10 bg-vortex-ink/60 px-8 py-6">
              <Clock size={20} className="shrink-0 text-vortex-cyan" />
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">Service Hours</p>
                <p className="mt-1 font-heading text-xl font-bold text-white">Monday–Friday, 8:00 AM – 8:00 PM</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5">
        <div className="mx-auto max-w-4xl px-6 py-20 lg:px-10">
          <Reveal>
            <Overline>Common Questions</Overline>
            <h2 className="mt-5 font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl">{`Vending in ${town.name}, answered.`}</h2>
            <Accordion type="single" collapsible className="mt-10" data-testid="town-faqs">
              {town.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger data-testid={`town-faq-${i}`} className="text-left font-heading text-lg font-bold tracking-tight text-white hover:text-vortex-cyan hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-400">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5 bg-vortex-surface/20" data-testid="town-nearby">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
          <Reveal>
            <Overline>Nearby Towns We Serve</Overline>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {nearby.map((a) => (
                <Link
                  key={a.slug}
                  to={TOWN_PAGE_SLUGS.has(a.slug) ? `/areas/${a.slug}` : "/service-area"}
                  className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 font-mono text-[11px] tracking-[0.15em] uppercase text-slate-400 transition-all duration-300 hover:border-vortex-cyan/50 hover:text-vortex-cyan"
                >
                  <MapPin size={11} />
                  {a.name}
                </Link>
              ))}
              <Link
                to="/service-area"
                className="flex items-center gap-2 rounded-full border border-vortex-cyan/40 px-4 py-2 font-mono text-[11px] tracking-[0.15em] uppercase text-vortex-cyan transition-all duration-300 hover:border-vortex-cyan"
              >
                Full service area
                <ArrowRight size={11} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
        <Reveal>
          <h2 className="max-w-3xl font-heading text-4xl font-black tracking-tighter text-white sm:text-5xl">
            {`IN ${town.name.toUpperCase()}? `}<span className="text-vortex-cyan">LET'S STOCK YOUR SPACE.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              data-testid="town-cta-call"
              href="tel:+16109838060"
              className="group flex items-center gap-3 rounded-full bg-vortex-cyan px-10 py-5 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_50px_rgba(92,179,226,0.5)]"
            >
              <Phone size={16} />
              Call (610) 983-8060
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default TownPage;
